import React, { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ALLOWED_IMAGE_ACCEPT_ATTR,
  dataTransferHasFiles,
  isAllowedImageFile,
} from "../../../utils/imageUploadValidation";
import Modal from "../../../componentLibrary/Modal";
import { ImageI } from "../../../types";
import Input from "../../../componentLibrary/Input";
import Combobox from "../../../componentLibrary/Combobox";
import Button from "../../../componentLibrary/Button";
import { ImageTypeEnum } from "../../../types";
import { useNotification } from "../../../contexts/NotificationContext";
import { useUpdateImage } from "../../../api/mutations";
import { getRbacErrorMessage } from "../../../constants/rbacMessages";

type EditImageModalProps = {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  setSelectedImage: (image: ImageI) => void;
  selectedImage: ImageI;
};

interface EditImageData {
  name: string;
  alt: string;
  type: ImageTypeEnum;
  image: File | null;
}

const EditImageModal = ({
  isModalOpen,
  setIsModalOpen,
  setSelectedImage,
  selectedImage,
}: EditImageModalProps) => {
  const { addNotification } = useNotification();
  const [editData, setEditData] = useState<EditImageData>({
    name: selectedImage?.name,
    alt: selectedImage?.alt,
    type: selectedImage?.type,
    image: null,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFileDragOver, setIsFileDragOver] = useState(false);

  const queryClient = useQueryClient();

  const updateMutation = useUpdateImage({
    onSuccess: async () => {
      addNotification("success", "Image updated successfully");
      setIsModalOpen(false);
      setSelectedImage(null);
      await queryClient.invalidateQueries({ queryKey: ["images"] });
    },
    onError: (error) => {
      addNotification("error", getRbacErrorMessage(error));
    },
  });

  const clearFileDragOver = () => setIsFileDragOver(false);

  const applyPickedFile = (selectedFile: File | undefined) => {
    if (!selectedFile) return;
    if (isAllowedImageFile(selectedFile)) {
      setEditData((prev) => ({ ...prev, image: selectedFile }));
    } else {
      setEditData((prev) => ({ ...prev, image: null }));
      addNotification(
        "error",
        "Please select a valid image file (JPEG or PNG or SVG).",
      );
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    applyPickedFile(event.target.files?.[0]);
  };

  const handleReplaceZoneDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dataTransferHasFiles(e.dataTransfer)) return;
    setIsFileDragOver(true);
  };

  const handleReplaceZoneDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    clearFileDragOver();
  };

  const handleReplaceZoneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dataTransferHasFiles(e.dataTransfer)) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleReplaceZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearFileDragOver();
    applyPickedFile(e.dataTransfer.files?.[0]);
  };

  const handleEditSubmit = () => {
    if (!selectedImage || !editData.name.trim() || !editData.alt.trim()) {
      addNotification("error", "Name and alt text are required");
      return;
    }
    const formData = new FormData();
    if (editData.image) {
      formData.append("image", editData.image);
    }
    formData.append("name", editData.name);
    formData.append("alt", editData.alt);
    formData.append("type", editData.type);
    updateMutation.mutate({
      id: selectedImage._id!,
      data: formData,
    });
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Modal
      title="Edit Image"
      size="md"
      isOpen={isModalOpen}
      onCloseModal={() => {
        clearFileDragOver();
        setIsModalOpen(false);
        setSelectedImage(null);
      }}
      height="fit-content"
    >
      {selectedImage && (
        <div className="space-y-4">
          <div
            className={`flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-3 transition-colors ${
              isFileDragOver ? "border-blue-500 bg-blue-50" : "border-gray-200"
            }`}
            onDragEnter={handleReplaceZoneDragEnter}
            onDragLeave={handleReplaceZoneDragLeave}
            onDragOver={handleReplaceZoneDragOver}
            onDrop={handleReplaceZoneDrop}
          >
            <div className="w-20 h-20 bg-gray-50 rounded-lg overflow-hidden">
              <img
                src={
                  editData.image
                    ? URL.createObjectURL(editData.image)
                    : selectedImage.src
                }
                alt={selectedImage.alt}
                className="w-full h-full object-cover"
              />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept={ALLOWED_IMAGE_ACCEPT_ATTR}
            />
            <Button
              variant="secondary"
              onClick={handleButtonClick}
              type="button"
              size="sm"
            >
              Replace Image
            </Button>
          </div>
          <Input
            label="Name"
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            placeholder="Enter image name"
            required
          />

          <Input
            label="Alt Text"
            value={editData.alt}
            onChange={(e) => setEditData({ ...editData, alt: e.target.value })}
            placeholder="Enter alt text"
            required
          />

          {selectedImage.type !== ImageTypeEnum.THUMBNAIL && (
            <div>
              <label className="block font-bold mb-2">Type</label>
              <Combobox
                value={{ value: editData.type, label: editData.type }}
                onChange={(option) =>
                  setEditData({
                    ...editData,
                    type: option.value as ImageTypeEnum,
                  })
                }
                options={[
                  { value: ImageTypeEnum.ICON, label: "Icon" },
                  { value: ImageTypeEnum.ILLUSTRATION, label: "Illustration" },
                ]}
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedImage(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleEditSubmit}
              loading={updateMutation.isPending}
              disabled={!editData.name.trim() || !editData.alt.trim()}
            >
              Update
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EditImageModal;
